import { Box, Checkbox, Flex, Highlight, Text } from '@chakra-ui/react';
import { Row } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { ChangeEvent, MouseEvent, memo, useCallback, useMemo } from 'react';
import { Avatar } from 'ui-components';
import { HighlightSearchTerm } from '~/components/HighlightSearchTerm';
import { ManagerDropdown } from '~/components/ManagerDropdown';
import { useTableStore } from '~/components/Table/table.store';
import { RosterViewDropdownMenu } from '~/components/TalentDropdownMenu';
import { FEATURE_FLAGS } from '~/constants';
import { routes } from '~/core/router';
import { getMainPlatformData } from '~/core/utils';
import { trackEvent } from '~/core/utils/trackEvent';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useSearchTalentStore } from '~/screens/MyAgency/store';
import { useCreatorStore } from '~/store/creator.store';
import { ICreator, ManagerShortType } from '~/types';
import { convertPxToRems } from '~/utils-ts';

import { useSavedListsStore } from '../../store/useSavedListsStore';
import { profileNameLinkStyle } from '../styles';
import { myAgencyListRowTrackId } from './constants';

interface ClientNameCellProps {
  row: Row<ICreator>;
  isShared: boolean;
  isSelectorActive?: boolean;
  onRemoveFromList?: (creator: ICreator) => void | Promise<void>;
}

export const ClientNameCell: React.FC<ClientNameCellProps> = memo(
  ({ row, isShared, isSelectorActive = true, onRemoveFromList }) => {
    const router = useRouter();

    const { toggleCreator, creators } = useSavedListsStore();

    const savedListsFeatureFlag = useFeatureFlag(FEATURE_FLAGS.foamSavedLists);
    const isNewProfilesEnabled =
      useFeatureFlag(FEATURE_FLAGS.foamSharedTalentProfile) === 'enabled';
    const isSavedListsEnabled = savedListsFeatureFlag === 'enabled';
    const { hoveringRow } = useTableStore();
    const { query } = useSearchTalentStore();

    const { original: creator } = row;

    const creatorStore = useCreatorStore();

    const isGlobalSearchPage = router.pathname === routes.globalSearch;

    /**
     * CroppedAvatar will use temporal blob that is generated from the upload component
     */
    const croppedAvatarURL = creatorStore.getCropperImage(creator?.id ?? '');

    const handleSelectCreator = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        toggleCreator(creator, isChecked);
      },
      [creator, toggleCreator],
    );

    const handleContainerClick = useCallback(
      (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
        if (isSavedListsEnabled) {
          event.stopPropagation();
        }
      },
      [isSavedListsEnabled],
    );

    const {
      avatar = '',
      name = '',
      handle = '',
      email = '',
    } = getMainPlatformData(creator);

    const handleCreatorClick = useCallback(async () => {
      trackEvent('foam-view-creator-from-talent-list-view');

      if (!creator) return;
      if (isShared) {
        if (isNewProfilesEnabled) {
          router.push(
            `https://${creator.slug}.${process.env.NEXT_PUBLIC_SHARED_PROFILES_HOST}`,
          );
          return;
        }
        router.push(routes.sharedProfileOverview(creator.slug || ''));

        return;
      }

      router.push(routes.profileOverview(creator?.slug || ''));
    }, [creator, isShared, router, isNewProfilesEnabled]);

    const isChecked = useMemo(
      () => !!creators.find((c) => c.id === creator.id),
      [creator.id, creators],
    );

    const shouldDisplayCheckbox = useCallback(() => {
      if (isChecked) {
        return true;
      }

      return hoveringRow === row.id && isSavedListsEnabled && !isShared;
    }, [hoveringRow, isChecked, isSavedListsEnabled, isShared, row.id]);

    return (
      <Flex
        backgroundColor="inherit"
        flexDirection="row-reverse"
        justifyContent="flex-end"
        width={{
          sm: convertPxToRems(80),
          md: convertPxToRems(220),
        }}
        gap={1}
        _hover={{
          '.client-name-icon-container': {
            backgroundColor: 'neutral.100',
          },
        }}
        _active={{
          '.client-name-icon-container': {
            backgroundColor: '#FFFAF5',
          },
        }}
      >
        <Flex alignItems="center" gap={3}>
          {shouldDisplayCheckbox() && isSelectorActive ? (
            <Box marginLeft={2} onClick={handleContainerClick} paddingRight={2}>
              <Checkbox
                onChange={handleSelectCreator}
                isChecked={isChecked}
                track-id="foam-talent-select-creator"
              />
            </Box>
          ) : (
            <Avatar
              alt={name}
              src={croppedAvatarURL ?? avatar}
              size="sm"
              data-testid="client-avatar"
            />
          )}
          <Box
            onClick={handleCreatorClick}
            style={{ textDecoration: 'none', cursor: 'pointer' }}
            track-id="foam-view-creator-from-talent-stack-view"
          >
            <Text
              color="primary.500"
              fontSize="md"
              fontWeight="semibold"
              display={{
                base: 'none',
                md: 'block',
              }}
              lineHeight={convertPxToRems(14)}
              marginRight={convertPxToRems(8)}
              track-id={myAgencyListRowTrackId.creatorName}
              {...profileNameLinkStyle}
            >
              {isGlobalSearchPage ? (
                <HighlightSearchTerm>
                  {name || handle || email}
                </HighlightSearchTerm>
              ) : (
                <Highlight
                  query={query}
                  styles={{
                    color: 'secondary.500',
                  }}
                >
                  {name || handle || email}
                </Highlight>
              )}
            </Text>
          </Box>
        </Flex>
        <Flex
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="client-name-icon-container"
          display="flex"
          flexDirection={{ base: 'column', md: 'row' }}
          backgroundColor="inherit"
        >
          {!isShared ? (
            <RosterViewDropdownMenu
              creator={creator}
              isShared={isShared}
              onRemoveFromList={onRemoveFromList}
            />
          ) : (
            <ManagerDropdown
              manager={creator.manager as ManagerShortType}
              isShared={isShared}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
